import { z } from "zod";
import { UserProfile } from "./account.js";

export { MIN_SEARCH_QUERY_LENGTH, SEARCH_PAGE_SIZE } from "@proef/domain";

export const FriendshipStatus = z.enum(["pending", "accepted", "blocked"]);
export type FriendshipStatus = z.infer<typeof FriendshipStatus>;

/** POST /v1/friends/requests -- offline-queueable, so it carries a client-generated id for
 * exactly-once semantics on reconnect (design.md's "idempotent offline-queued writes"). */
export const SendFriendRequestRequest = z.object({
  clientRequestId: z.uuid(),
  targetUserId: z.string().min(1),
});
export type SendFriendRequestRequest = z.infer<typeof SendFriendRequestRequest>;

/** `ApiError` body of a refused friend request (429): why, and how long until it may be tried again.
 * `decline_cooldown` is the 30 days after a decline, `daily_limit` the 50 a day, `rate_limit` the burst. */
export const FriendRequestRefusal = z.object({
  error: z.string(),
  code: z.enum(["decline_cooldown", "daily_limit", "rate_limit"]),
  retryAfterSeconds: z.number().int().positive(),
});
export type FriendRequestRefusal = z.infer<typeof FriendRequestRefusal>;

export const FriendshipView = z.object({
  id: z.string().min(1),
  user: UserProfile,
  status: FriendshipStatus,
  /** Whose turn it is to act, from the viewer's side. */
  direction: z.enum(["incoming", "outgoing", "mutual"]),
  createdAt: z.iso.datetime(),
});
export type FriendshipView = z.infer<typeof FriendshipView>;

/** GET /v1/friends -- `loaded: true` distinguishes "confirmed empty" from a stale cached `[]`
 * during offline load. */
export const FriendList = z.object({
  loaded: z.literal(true),
  items: z.array(FriendshipView),
  nextCursor: z.string().nullable(),
});
export type FriendList = z.infer<typeof FriendList>;

/**
 * Another person as any social surface may show them: enough to recognise and add someone, never
 * their email, friend list, groups or activity. Search results, block lists and invites use it.
 */
export const PublicUser = z.object({
  id: z.string().min(1),
  handle: z.string().min(1).nullable(),
  displayName: z.string().min(1).max(60),
  avatarUrl: z.url().nullable(),
});
export type PublicUser = z.infer<typeof PublicUser>;

/** What a route that removes a row answers with: there is no row left to describe. */
export const OkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof OkResponse>;

/** POST /v1/friends/requests/:id/<action> -- accept answers with the row, decline, cancel and
 * unfriend with `{ok:true}`, so a client parses both instead of failing on the second. */
export const FriendshipActionResponse = z.union([FriendshipView, OkResponse]);
export type FriendshipActionResponse = z.infer<typeof FriendshipActionResponse>;

/** GET /v1/blocks -- the people the caller blocked, identity fields only. */
export const BlockList = z.object({
  items: z.array(PublicUser),
});
export type BlockList = z.infer<typeof BlockList>;

export const UserSearchResponse = z.object({
  items: z.array(PublicUser),
  nextCursor: z.string().nullable(),
});
export type UserSearchResponse = z.infer<typeof UserSearchResponse>;
